import { defineStore } from 'pinia'
import { getTableMapDefaultSize, tableTemplateTableTypes } from 'src/common/constants'
import type {
  ITableTemplateElement,
  ITableTemplateFloor,
  TableTemplateAlignDirection,
  TableTemplateElementType,
} from 'src/common/interfaces'

interface IState {
  floors: ITableTemplateFloor[]
  activeFloorId: number
  selectedElementIds: string[]
  zoom: number
  history: ITableTemplateFloor[][]
  historyIndex: number
  pendingDelete: string[] | null
  focusElementId: string | null
}

const MAX_HISTORY = 50

function isTableType(type: TableTemplateElementType): boolean {
  return tableTemplateTableTypes.includes(type)
}

function getVisualBounds(el: ITableTemplateElement) {
  const scale = el.scale ?? 1
  const w = el.width * scale
  const h = el.height * scale
  const cx = el.x + w / 2
  const cy = el.y + h / 2
  if (isTableType(el.type) || !el.rotation) {
    return { x: el.x, y: el.y, width: w, height: h, cx, cy }
  }
  const rad = (el.rotation * Math.PI) / 180
  const vw = Math.abs(w * Math.cos(rad)) + Math.abs(h * Math.sin(rad))
  const vh = Math.abs(w * Math.sin(rad)) + Math.abs(h * Math.cos(rad))
  return { x: cx - vw / 2, y: cy - vh / 2, width: vw, height: vh, cx, cy }
}

export const useTableTemplateStore = defineStore('tableTemplate', {
  state: (): IState => ({
    floors: [],
    activeFloorId: 0,
    selectedElementIds: [],
    zoom: 1,
    history: [[]],
    historyIndex: 0,
    pendingDelete: null,
    focusElementId: null,
  }),

  getters: {
    activeFloor(state): ITableTemplateFloor | undefined {
      return state.floors.find((f) => f.id === state.activeFloorId)
    },
    activeElements(state): ITableTemplateElement[] {
      return state.floors.find((f) => f.id === state.activeFloorId)?.json_data ?? []
    },
    selectedElement(state): ITableTemplateElement | undefined {
      if (state.selectedElementIds.length !== 1) return undefined
      const elements = state.floors.find((f) => f.id === state.activeFloorId)?.json_data ?? []
      return elements.find((e) => e.element_id === state.selectedElementIds[0])
    },
    selectedElements(state): ITableTemplateElement[] {
      const ids = new Set(state.selectedElementIds)
      const elements = state.floors.find((f) => f.id === state.activeFloorId)?.json_data ?? []
      return elements.filter((e) => ids.has(e.element_id))
    },
    isMultiSelect(state): boolean {
      return state.selectedElementIds.length > 1
    },
    canUndo(state): boolean {
      return state.historyIndex > 0
    },
    canRedo(state): boolean {
      return state.historyIndex < state.history.length - 1
    },
  },

  actions: {
    init(floors: ITableTemplateFloor[]) {
      this.floors = floors.map((f) => ({ ...f, json_data: f.json_data ?? [] }))
      this.activeFloorId = floors.length > 0 ? floors[0].id : 0
      this.selectedElementIds = []
      this.history = [JSON.parse(JSON.stringify(this.floors))]
      this.historyIndex = 0
    },

    setActiveFloor(id: number) {
      this.activeFloorId = id
      this.selectedElementIds = []
    },

    addElement(type: TableTemplateElementType, shape: string, value: number, x: number, y: number) {
      const floor = this.floors.find((f) => f.id === this.activeFloorId)
      if (!floor) return
      const size = getTableMapDefaultSize(type, shape, value)
      const el: ITableTemplateElement = {
        element_id: `el-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        type,
        shape,
        value,
        x,
        y,
        rotation: 0,
        floor_id: floor.id,
        table_number: isTableType(type) ? this._nextTableNumber() : 0,
        section_id: 0,
        width: size.width,
        height: size.height,
        z_index: type === 'flooring' ? 499 : 500,
        scale: 1,
        no_seat: false,
        render_x: null,
        render_y: null,
        is_circle: false,
      }
      floor.json_data.push(el)
      this.selectedElementIds = [el.element_id]
      this._pushHistory()
    },

    moveElements(positions: Record<string, { x: number; y: number }>) {
      const floor = this.floors.find((f) => f.id === this.activeFloorId)
      if (!floor) return
      for (const el of floor.json_data) {
        const pos = positions[el.element_id]
        if (pos) {
          el.x = pos.x
          el.y = pos.y
        }
      }
    },

    resizeAndMoveElement(id: string, x: number, y: number, width: number, height: number) {
      const el = this._findElement(id)
      if (el) {
        el.x = x
        el.y = y
        el.width = width
        el.height = height
      }
    },

    finishMove() {
      this._pushHistory()
    },

    updateElement(id: string, patch: Partial<ITableTemplateElement>) {
      const el = this._findElement(id)
      if (el) {
        Object.assign(el, patch)
        this._pushHistory()
      }
    },

    setElementScale(id: string, scale: number) {
      const el = this._findElement(id)
      if (!el) return
      el.scale = scale
    },

    setSelectedElementsScale(scale: number) {
      const elements = this.selectedElements
      if (!elements.length) return
      const minX = Math.min(...elements.map((e) => e.x))
      const minY = Math.min(...elements.map((e) => e.y))
      const currentScale = elements[0].scale ?? 1
      const factor = scale / currentScale
      elements.forEach((el) => {
        el.x = minX + (el.x - minX) * factor
        el.y = minY + (el.y - minY) * factor
        el.scale = scale
      })
    },

    rotateSelectedElements() {
      const elements = this.selectedElements
      if (!elements.length) return

      if (elements.length === 1) {
        elements[0].rotation = (elements[0].rotation + 90) % 360
        this._pushHistory()
        return
      }

      // Measure pre-rotation gaps along both axes independently
      const byX = [...elements].sort((a, b) => getVisualBounds(a).x - getVisualBounds(b).x)
      let totalHGap = 0
      for (let i = 1; i < byX.length; i++) {
        const ba = getVisualBounds(byX[i - 1])
        const bb = getVisualBounds(byX[i])
        totalHGap += bb.x - (ba.x + ba.width)
      }
      const avgHGap = totalHGap / (byX.length - 1)

      const byY = [...elements].sort((a, b) => getVisualBounds(a).y - getVisualBounds(b).y)
      let totalVGap = 0
      for (let i = 1; i < byY.length; i++) {
        const ba = getVisualBounds(byY[i - 1])
        const bb = getVisualBounds(byY[i])
        totalVGap += bb.y - (ba.y + ba.height)
      }
      const avgVGap = totalVGap / (byY.length - 1)

      // Rotate each element in place
      elements.forEach((el) => {
        el.rotation = (el.rotation + 90) % 360
      })

      // Redistribute H if elements were horizontally separated before rotation
      if (avgHGap >= 0) {
        byX.sort((a, b) => getVisualBounds(a).x - getVisualBounds(b).x)
        const firstH = getVisualBounds(byX[0])
        let cursor = firstH.x + firstH.width + avgHGap
        for (let i = 1; i < byX.length; i++) {
          const vb = getVisualBounds(byX[i])
          const w = byX[i].width * (byX[i].scale ?? 1)
          byX[i].x = cursor + vb.width / 2 - w / 2
          cursor += vb.width + avgHGap
        }
      }

      // Redistribute V if elements were vertically separated before rotation
      if (avgVGap >= 0) {
        byY.sort((a, b) => getVisualBounds(a).y - getVisualBounds(b).y)
        const firstV = getVisualBounds(byY[0])
        let cursor = firstV.y + firstV.height + avgVGap
        for (let i = 1; i < byY.length; i++) {
          const vb = getVisualBounds(byY[i])
          const h = byY[i].height * (byY[i].scale ?? 1)
          byY[i].y = cursor + vb.height / 2 - h / 2
          cursor += vb.height + avgVGap
        }
      }

      this._pushHistory()
    },

    requestDelete(ids: string[]) {
      this.pendingDelete = ids
    },

    confirmDelete() {
      if (!this.pendingDelete) return
      const ids = this.pendingDelete
      this.pendingDelete = null
      this.deleteElements(ids)
    },

    cancelDelete() {
      this.pendingDelete = null
    },

    selectElement(id: string | null) {
      this.selectedElementIds = id ? [id] : []
    },

    toggleElementSelection(id: string) {
      const idx = this.selectedElementIds.indexOf(id)
      if (idx === -1) {
        this.selectedElementIds = [...this.selectedElementIds, id]
      } else {
        this.selectedElementIds = this.selectedElementIds.filter((eid) => eid !== id)
      }
    },

    deleteElements(ids: string[]) {
      const floor = this.floors.find((f) => f.id === this.activeFloorId)
      if (!floor) return
      const idSet = new Set(ids)
      floor.json_data = floor.json_data.filter((e) => !idSet.has(e.element_id))
      this.selectedElementIds = this.selectedElementIds.filter((id) => !idSet.has(id))
      this._pushHistory()
    },

    duplicateElements(ids: string[]) {
      const floor = this.floors.find((f) => f.id === this.activeFloorId)
      if (!floor) return
      const newIds: string[] = []
      for (const id of ids) {
        const el = floor.json_data.find((e) => e.element_id === id)
        if (!el) continue
        const newEl: ITableTemplateElement = {
          ...el,
          element_id: `el-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
          x: el.x + 30,
          y: el.y + 30,
          table_number: isTableType(el.type) ? this._nextTableNumber() : el.table_number,
        }
        floor.json_data.push(newEl)
        newIds.push(newEl.element_id)
      }
      this.selectedElementIds = newIds
      this._pushHistory()
    },

    distributeWithGap(gap: number, axis: 'horizontal' | 'vertical') {
      const elements = [...this.selectedElements]
      if (elements.length < 2) return
      if (axis === 'horizontal') {
        elements.sort((a, b) => getVisualBounds(a).x - getVisualBounds(b).x)
        const first = getVisualBounds(elements[0])
        let cursor = first.x + first.width + gap
        for (let i = 1; i < elements.length; i++) {
          const vb = getVisualBounds(elements[i])
          const w = elements[i].width * (elements[i].scale ?? 1)
          elements[i].x = cursor + vb.width / 2 - w / 2
          cursor += vb.width + gap
        }
      } else {
        elements.sort((a, b) => getVisualBounds(a).y - getVisualBounds(b).y)
        const first = getVisualBounds(elements[0])
        let cursor = first.y + first.height + gap
        for (let i = 1; i < elements.length; i++) {
          const vb = getVisualBounds(elements[i])
          const h = elements[i].height * (elements[i].scale ?? 1)
          elements[i].y = cursor + vb.height / 2 - h / 2
          cursor += vb.height + gap
        }
      }
      this._pushHistory()
    },

    alignElements(direction: TableTemplateAlignDirection) {
      const elements = this.selectedElements
      if (elements.length < 2) return
      const bounds = elements.map((e) => getVisualBounds(e))
      const minX = Math.min(...bounds.map((b) => b.x))
      const maxRight = Math.max(...bounds.map((b) => b.x + b.width))
      const minY = Math.min(...bounds.map((b) => b.y))
      const maxBottom = Math.max(...bounds.map((b) => b.y + b.height))
      const centerX = (minX + maxRight) / 2
      const centerY = (minY + maxBottom) / 2
      elements.forEach((el, i) => {
        const vb = bounds[i]
        const w = el.width * (el.scale ?? 1)
        const h = el.height * (el.scale ?? 1)
        if (direction === 'left') el.x = minX + vb.width / 2 - w / 2
        else if (direction === 'center-h') el.x = Math.round(centerX - w / 2)
        else if (direction === 'right') el.x = maxRight - vb.width / 2 - w / 2
        else if (direction === 'top') el.y = minY + vb.height / 2 - h / 2
        else if (direction === 'center-v') el.y = Math.round(centerY - h / 2)
        else if (direction === 'bottom') el.y = maxBottom - vb.height / 2 - h / 2
      })
      this._pushHistory()
    },

    setZoom(zoom: number) {
      this.zoom = Math.max(0.15, Math.min(3, zoom))
    },

    reorderElements(orderedIds: string[]) {
      // orderedIds[0] = frontmost (highest z_index)
      const floor = this.floors.find((f) => f.id === this.activeFloorId)
      if (!floor) return
      orderedIds.forEach((id, i) => {
        const el = floor.json_data.find((e) => e.element_id === id)
        if (el) el.z_index = orderedIds.length - i
      })
      this._pushHistory()
    },

    focusElement(id: string) {
      this.selectedElementIds = [id]
      this.focusElementId = id
    },

    clearFocusElementId() {
      this.focusElementId = null
    },

    undo() {
      if (!this.canUndo) return
      this.historyIndex--
      this.floors = JSON.parse(JSON.stringify(this.history[this.historyIndex]))
      this.selectedElementIds = []
    },

    redo() {
      if (!this.canRedo) return
      this.historyIndex++
      this.floors = JSON.parse(JSON.stringify(this.history[this.historyIndex]))
      this.selectedElementIds = []
    },

    _findElement(id: string): ITableTemplateElement | undefined {
      const floor = this.floors.find((f) => f.id === this.activeFloorId)
      return (floor?.json_data ?? []).find((e) => e.element_id === id)
    },

    _nextTableNumber(): number {
      const all = this.floors.flatMap((f) => f.json_data)
      const nums = all.filter((e) => isTableType(e.type) && e.table_number).map((e) => e.table_number)
      return nums.length > 0 ? Math.max(...nums) + 1 : 1
    },

    _pushHistory() {
      const snapshot = JSON.parse(JSON.stringify(this.floors))
      this.history = this.history.slice(0, this.historyIndex + 1)
      this.history.push(snapshot)
      if (this.history.length > MAX_HISTORY) {
        this.history.shift()
      } else {
        this.historyIndex++
      }
    },
  },
})
